import api from './client';
import type {
  SystemHealth,
  SchedulerJob,
  ReschedulePayload,
  JobRunLog,
  LogType,
  LogEntry,
  LogStats,
  SettingsGroup,
} from './types';

// ── Settings ─────────────────────────────────────────────────────────────────

export const fetchAllSettings = () =>
  api.get<{ groups: SettingsGroup[] }>("/settings/all").then((r) => r.data);

export const updateSettings = (updates: Record<string, unknown>) =>
  api.put<{ status: string; applied: Record<string, unknown>; errors: Record<string, string> }>("/settings/update", updates).then((r) => r.data);

export const resetSettingsKeys = (keys: string[]) =>
  api.post<{ status: string; reset: Record<string, unknown> }>("/settings/reset", { keys }).then((r) => r.data);

// ── Emergency Stop ───────────────────────────────────────────────────────────

export const fetchEmergencyStopStatus = () =>
  api.get<{ active: boolean }>("/settings/emergency-stop/status").then((r) => r.data);

export const emergencyStop = () =>
  api.post<{
    status: string;
    active: boolean;
    auto_trade_enabled: boolean;
    auto_banknifty_enabled: boolean;
    paused_jobs: number;
    closed_bn_groups: number;
    rejected_signals: number;
    message: string;
  }>("/settings/emergency-stop").then((r) => r.data);

export const emergencyStopDisable = () =>
  api.post<{
    status: string;
    active: boolean;
    auto_trade_enabled: boolean;
    auto_banknifty_enabled: boolean;
    resumed_jobs: number;
    message: string;
  }>("/settings/emergency-stop/disable").then((r) => r.data);

// ── System Health ────────────────────────────────────────────────────────────

export const fetchSystemHealth = () =>
  api.get<SystemHealth>("/health/system").then((r) => r.data);

// ── Scheduler ────────────────────────────────────────────────────────────────

export const fetchSchedulerJobs = () =>
  api.get<SchedulerJob[]>("/scheduler/jobs").then((r) => r.data);

export const toggleSchedulerJob = (id: string) =>
  api.post<{ id: string; enabled: boolean; next_run: string | null }>(
    `/scheduler/jobs/${id}/toggle`
  ).then((r) => r.data);

export const rescheduleJob = (id: string, payload: ReschedulePayload) =>
  api.post<SchedulerJob>(`/scheduler/jobs/${id}/reschedule`, payload).then((r) => r.data);

export const runJobNow = (id: string) =>
  api.post<{ status: string; job_id: string; log: JobRunLog }>(
    `/scheduler/jobs/${id}/run`
  ).then((r) => r.data);

export const fetchJobLogs = (id: string) =>
  api.get<JobRunLog[]>(`/scheduler/jobs/${id}/logs`).then((r) => r.data);

// ── Structured Logs ──────────────────────────────────────────────────────────

export const fetchLogDates = () =>
  api.get<string[]>("/logs/dates").then((r) => r.data);

export const fetchLogEntries = (
  date: string,
  params?: { type?: LogType; search?: string; limit?: number; offset?: number }
) =>
  api.get<LogEntry[]>(`/logs/${date}`, { params }).then((r) => r.data);

export const fetchLogStats = (date: string) =>
  api.get<LogStats>(`/logs/stats/${date}`).then((r) => r.data);

// ── Claude Chat ──────────────────────────────────────────────────────────────

// Note: streaming chat uses fetch() directly in ChatPanel (not axios) for SSE support.
// These helpers are for the one-shot brief/review calls only.

export const triggerMorningBrief = () =>
  api.post<{ brief: string; type: string }>("/chat/brief", {}, { timeout: 60000 }).then((r) => r.data);

export const triggerEodReview = () =>
  api.post<{ review: string; type: string }>("/chat/eod", {}, { timeout: 60000 }).then((r) => r.data);
